import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Check, Clock, Users, ArrowRight, Phone, Mail } from "lucide-react";
import { IMAGES, SERVICE_DETAILS, CONTACT } from "../mock";
import Seo from "../components/Seo";

const ServicePage = ({ slug }) => {
  const svc = SERVICE_DETAILS[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug]);

  if (!svc) return <Navigate to="/" replace />;

  // Parse one or more dollar amounts from svc.price (e.g. "$165", "$749 / $949", "$1,200 / $1,600 / $2,100")
  const amounts = (svc.price.match(/\$\s?([\d,]+)/g) || []).map((m) =>
    parseInt(m.replace(/[^\d]/g, ""), 10)
  );
  // Labels for each tier (parse from priceNote, e.g. "4 Hours / 6 Hours · Up to 3 guests")
  const tierLabels = (() => {
    if (amounts.length <= 1) return [];
    const beforeDot = (svc.priceNote || "").split("·")[0] || "";
    const parts = beforeDot.split("/").map((s) => s.trim()).filter(Boolean);
    if (parts.length === amounts.length) return parts;
    return amounts.map(() => "");
  })();
  const payHref = (amt) => `${CONTACT.paypal}/${amt}USD`;

  return (
    <div className="bg-stone-50">
      <Seo
        title={svc.title}
        description={`${svc.description.slice(0, 155)}`}
        path={`/${slug}`}
        image={IMAGES[svc.hero]}
        service={svc.title}
        price={amounts[0]}
      />
      {/* Hero */}
      <section className="relative h-[68vh] min-h-[480px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url(${IMAGES[svc.hero]})`,
            backgroundPosition: "center 25%",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/30 to-slate-900/80" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-16 max-w-7xl mx-auto px-6">
          <Link to="/" className="text-white/80 hover:text-white text-sm mb-4 inline-flex items-center gap-1">
            ← Back to Home
          </Link>
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-semibold mb-3">
            {svc.tagline}
          </div>
          <h1
            className="text-5xl md:text-7xl text-white drop-shadow-2xl max-w-3xl"
            style={{ fontFamily: "'Pacifico', cursive", lineHeight: 1.05 }}
          >
            {svc.title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 reveal">
            <h2 className="text-3xl md:text-4xl text-slate-900 mb-5" style={{ fontFamily: "'Pacifico', cursive" }}>
              About this experience
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">
              {svc.description}
            </p>

            <h3 className="text-xl font-bold text-slate-900 mb-3">What's Included</h3>
            <ul className="grid sm:grid-cols-2 gap-2 mb-10">
              {svc.includes.map((it) => (
                <li key={it} className="flex items-start gap-2 text-slate-700">
                  <Check className="text-orange-500 shrink-0 mt-0.5" size={18} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            <h3 className="text-xl font-bold text-slate-900 mb-4">Gallery</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {svc.gallery.map((g, i) => (
                <div
                  key={g + i}
                  className="aspect-square overflow-hidden rounded-xl group"
                >
                  <img
                    src={IMAGES[g]}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Sticky booking card */}
          <aside className="lg:sticky lg:top-28 self-start reveal">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6">
              <div className="text-xs uppercase tracking-wider text-orange-600 font-bold mb-1">From</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-4xl font-bold text-slate-900">{svc.price}</span>
              </div>
              <div className="text-sm text-slate-500 mb-5">{svc.priceNote}</div>

              <div className="flex items-center gap-4 text-sm text-slate-600 mb-6 pb-6 border-b border-slate-100">
                <div className="flex items-center gap-1.5">
                  <Clock size={16} className="text-orange-500" /> {svc.duration}
                </div>
                <div className="flex items-center gap-1.5">
                  <Users size={16} className="text-orange-500" /> Small groups
                </div>
              </div>

              {amounts.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {amounts.map((amt, i) => (
                    <a
                      key={amt + "-" + i}
                      href={payHref(amt)}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between gap-2 px-5 py-3.5 bg-[#0070ba] hover:bg-[#005ea6] text-white font-bold rounded-full transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
                          <path d="M7.4 21l.6-3.9h2.4c4 0 6.4-2 7-5.6.2-1.1.1-2-.3-2.8C16 6.9 14 6 11 6H6.6a.7.7 0 0 0-.7.6L4 19.5c0 .3.2.6.5.6h2.4l.5.9zm3.6-12.5h2.2c1.7 0 2.6.5 2.9 1.5.1.4.1.9 0 1.5-.4 2-1.6 3-3.7 3H10l-.2 1.2-1 6.3H6.6L8 9c0-.3.3-.5.5-.5h2.5z"/>
                        </svg>
                        Pay with PayPal
                      </span>
                      <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">
                        {tierLabels[i] ? `${tierLabels[i]} · ` : ""}${amt.toLocaleString()}
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  className="block text-center w-full px-5 py-3.5 bg-[#0070ba] hover:bg-[#005ea6] text-white font-bold rounded-full transition-colors mb-3"
                >
                  Call for pricing
                </a>
              )}
              <Link
                to="/book-today"
                className="block text-center w-full px-5 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full transition-colors mb-3"
              >
                Request Booking
              </Link>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold rounded-full transition-colors"
              >
                <Phone size={16} /> {CONTACT.phone}
              </a>

              <div className="mt-6 pt-5 border-t border-slate-100 text-sm text-slate-600 space-y-2">
                <div className="flex gap-2 items-start">
                  <Mail className="text-orange-500 mt-0.5" size={16} />
                  <a href={`mailto:${CONTACT.email}`} className="hover:text-orange-600">{CONTACT.email}</a>
                </div>
                <div className="text-xs text-slate-500">
                  Cancellation policy: 48 hours notice for full refund.
                </div>
              </div>
            </div>

            <Link
              to="/book-today"
              className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-700 hover:text-orange-600"
            >
              View all experiences <ArrowRight size={14} />
            </Link>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
